// src/components/FTSearchModal.jsx
import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {useSelector} from "react-redux";
import CMSearchPatterns from '../components/SearchPageComponents/CMSearchPatterns';
import GQPopup from '../components/GQPopups/GQPopup';

const useStyles = makeStyles(() => ({
  modalClassConDSI: { borderRadius: "8px",  width: "auto"},
  topBar: {
    padding: '12px 20px 0px 17px',
    borderBottom: 'none',
    width: '900px'
  },
  bodyPad: { padding: '15px', width: '900px' },
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '10px 16px'
  },
  btn: {
    padding: '8px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
    border: 'none',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      color: 'rgb(0 0 0 / 26%)'
    }
  },
  btnGrey: { background: '#e0e0e0', color: '#222' },
  btnBlue: { background: '#1976d2', color: '#fff' },
  btnOrange: { background: '#db862c', color: '#fff' }
}));

const widgetAtom ={
    searchInfo: {
      cm_filters: {}
    },
    layout: {
      errorIdList: [],
      isRedo: false
    }
}

const normalizeSeqType = (seqType) => {
  if (typeof seqType !== 'string') {
    return null;
  }

  const normalized = seqType.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (normalized === 'protein' || normalized === 'prt' || normalized === 'aa' || normalized.includes('protein') || normalized.includes('prt') || normalized.includes('amino')) {
    return 'protein';
  }

  if (normalized === 'nucleotide' || normalized === 'nuc' || normalized === 'nt' || normalized === 'dna' || normalized === 'rna' || normalized.includes('nucleotide') || normalized.includes('nuc') || normalized.includes('dna') || normalized.includes('rna')) {
    return 'nucleotide';
  }

  return null;
};

const normalizeWidgetState = (start, resultSetSeqType) => {
  const baseState = _.cloneDeep(widgetAtom);
  const fallbackSeqType = normalizeSeqType(resultSetSeqType) || 'protein';

  if (!start || typeof start !== 'object' || Array.isArray(start) || Object.keys(start).length === 0) {
    return {
      ...baseState,
      searchInfo: {
        ...baseState.searchInfo,
        qdb_seq_type: fallbackSeqType
      }
    };
  }

  const nextState = _.cloneDeep(start);

  if (!nextState.searchInfo || typeof nextState.searchInfo !== 'object' || Array.isArray(nextState.searchInfo)) {
    nextState.searchInfo = {};
  }

  if (nextState.searchInfo.cm_filters === undefined || Array.isArray(nextState.searchInfo.cm_filters)) {
    nextState.searchInfo.cm_filters = _.cloneDeep(widgetAtom.searchInfo.cm_filters);
  }

  const explicitSeqType = normalizeSeqType(nextState.searchInfo.qdb_seq_type);
  const savedFilterSeqType = normalizeSeqType(nextState.searchInfo.cm_filters?.qdb_seq_type);
  nextState.searchInfo.qdb_seq_type = explicitSeqType || savedFilterSeqType || fallbackSeqType;

  if (!nextState.layout || typeof nextState.layout !== 'object' || Array.isArray(nextState.layout)) {
    nextState.layout = _.cloneDeep(widgetAtom.layout);
  }

  if (!Array.isArray(nextState.layout.errorIdList)) {
    nextState.layout.errorIdList = [];
  }

  if (typeof nextState.layout.isRedo !== 'boolean') {
    nextState.layout.isRedo = false;
  }

  return {
    ...baseState,
    ...nextState,
    searchInfo: {
      ...baseState.searchInfo,
      ...nextState.searchInfo
    },
    layout: {
      ...baseState.layout,
      ...nextState.layout
    }
  };
};

const QuerySequencePatterns = ({ start, apply, cancel, isOpen, resultSetSeqType, existingCmFilters, hideFormat = [], availableModCodes }) => {
 
  const classes = useStyles();
  const toggle = useSelector(state => state?.setToggle?.toggle);

  const [widgetState, setWidgetState] = useState(() => normalizeWidgetState(start, resultSetSeqType));
  const [overrideName, setOverrideName] = useState(false);

  useEffect(() => {
      const normalizedState = normalizeWidgetState(start, resultSetSeqType);
      setWidgetState(normalizedState);
      setOverrideName(hasDuplicateFilterName(normalizedState?.searchInfo?.cm_filters?.filterName));
      
    
    }, [start, resultSetSeqType, existingCmFilters]);

  function handleClear() {
    setWidgetState(normalizeWidgetState(_.cloneDeep(widgetAtom), resultSetSeqType));
  }

  function hasDuplicateFilterName(filterName) {
    const enteredName = typeof filterName === 'string'
      ? filterName.trim()
      : '';

    if (!enteredName || !Array.isArray(existingCmFilters)) {
      return false;
    }

    return existingCmFilters.some((cmFilter) => (
      cmFilter &&
      typeof cmFilter === 'object' &&
      !Array.isArray(cmFilter) &&
      typeof cmFilter.filterName === 'string' &&
      cmFilter.filterName.trim() === enteredName
    ));
  }

  function handleSave() {
    if (hasDuplicateFilterName(widgetState?.searchInfo?.cm_filters?.filterName)) {
      setOverrideName(true);
      return;
    }

    apply(widgetState);
  }

  function handleOverrideSave() {
    setOverrideName(false);
    apply(widgetState);
  }

  function handleFilterNameChange(event) {
    const nextFilterName = event.target.value;

    setWidgetState({
      ...widgetState,
      searchInfo: {
        ...widgetState.searchInfo,
        cm_filters: {
          ...widgetState.searchInfo.cm_filters,
          filterName: nextFilterName
        }
      }
    });

    setOverrideName(hasDuplicateFilterName(nextFilterName));
  }

  return (
    <GQPopup
      show={isOpen}
      close={()=>{cancel()}}
      innerHtml={
        <div style={{ position: 'relative', paddingRight: "5px"}}>
          <div style={{display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center"}}>
          <h3 style={{width: "600px", padding: "15px", paddingLeft: "0px"}}>
            Define your Query sequence patterns
          </h3>
          <input
            type="text"
            placeholder="Name your template..."
            value={widgetState?.searchInfo?.cm_filters?.filterName || ""}
            onChange={handleFilterNameChange}
            style={{width: "300px", padding: "10px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc"}}
          />
          </div>
        <CMSearchPatterns
          qSeqInstead={true}
            hideTitle={true}
            hideTemplates={true}
            hideFormat={hideFormat}
            formJson={widgetState}
            setFormJson={(data)=>setWidgetState({...data})}
            availableModCodes={availableModCodes}
        />
        <hr style={{margin: "10px 0"}}/>
        <div className={classes.footerBar} style={{width: "100%", display: "flex", marginTop: "10px", marginBottom: "-10px"}}>
          <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'flex-start', height: '50px' }}>
              <button className={`${classes.btn} ${classes.btnGrey}`} onClick={()=>handleClear()}>
                  Reset
              </button>
          </div>
          <div style={{display: "flex", height: '50px', justifyContent: "space-between", alignItems: "center", gap: "10px"}}>
            <button className={`${classes.btn} ${classes.btnGrey}`} onClick={()=>{cancel()}}>
              Cancel
            </button>
            {widgetState?.searchInfo?.cm_filters?.filterName ? (
              overrideName ? (
                <button className={`${classes.btn} ${classes.btnOrange}`} style={{whiteSpace: "nowrap"}} onClick={handleOverrideSave}>
                  Overwrite Existing Filter
                </button>
              ) : (
                <button className={`${classes.btn} ${classes.btnBlue}`} style={{whiteSpace: "nowrap"}} onClick={handleSave}>
                  Save as New Filter
                </button>
              )
            ) : (
              <button className={`${classes.btn} ${classes.btnGrey}`} style={{whiteSpace: "nowrap"}} disabled onClick={handleSave}>
                Save as New Filter
              </button>
            )}
          </div>
        </div>
       </div>
      }/>
  );
};

export default QuerySequencePatterns;

